import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCheck, FaMagic, FaCut, FaImage, FaCreditCard } from 'react-icons/fa';
import { menHairstyles, womenHairstyles } from './HairstyleGallery';
import { Helmet } from 'react-helmet';
import Breadcrumbs from '../components/Breadcrumbs';
import FAQPageSchema from '../components/FAQPageSchema';

const Home = () => {
  const features = [
    {
      icon: <FaMagic className="text-4xl text-teal-500" />,
      title: "AI-Powered Transformation",
      description: "Experience the magic of AI as it transforms your photos with stunning new hairstyles in seconds."
    },
    {
      icon: <FaCut className="text-4xl text-teal-500" />,
      title: "Extensive Style Collection",
      description: "Choose from hundreds of trendy hairstyles for both men and women, or upload your own reference."
    },
    {
      icon: <FaImage className="text-4xl text-teal-500" />,
      title: "Realistic Results",
      description: "Our advanced AI ensures natural-looking results that match your facial features perfectly."
    }
  ];

  const pricingFeatures = [
    "High-quality AI-generated images",
    "Multiple style options",
    "Color variations",
    "Download in HD resolution",
    "Save to your gallery",
    "Share on social media"
  ];

  // Select 4 featured hairstyles (2 men, 2 women)
  const featuredStyles = [
    menHairstyles[2],  // Chalamet Flow
    womenHairstyles[0], // Pompadour
    menHairstyles[8],  // Pompadour
    womenHairstyles[12], // Short Bob
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Hairstyle Studio",
    "description": "Virtual hair salon powered by AI for trying on different hairstyles and haircuts",
    "applicationCategory": "Beauty & Lifestyle",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": features.map(f => f.title).join(", ")
  };

  return (
    <>
    <Helmet>
        <title>AI Hairstyle Studio - Virtual Hair Salon & Style Transformation</title>
        <meta name="description" content="Transform your look with AI Hairstyle Studio's virtual hair salon. Try on different hairstyles, explore haircuts, and experiment with colors instantly using AI technology." />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
  <Breadcrumbs />
  <FAQPageSchema />
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-screen flex items-center">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="/videos/hairstyle-bg.webm" type="video/webm" />
            Your browser does not support the video tag.
          </video>
          {/* Overlay with gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-gray-900/90 backdrop-blur-sm"></div>
        </div>

        {/* Content */}
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Let AI Hairstyle Studio 
              <span className = "bg-gradient-to-r from-[#4EC5C1] to-[#6FD4D0] text-transparent bg-clip-text inline-block">
                Transform Your Luxury
              </span>
               Look
            </h1>
            <p className="text-xl text-gray-200 mb-4">
             Experience the ultimate luxury with AI Hairstyle Studio, your virtual <a className="text-teal-500" href="https://www.aihairstylestudio.com/studio-hair-salon">studio hair salon</a> <br></br> Try on hairstyles, explore haircuts, and experiment with colors instantly.
            </p>
            <p className="text-lg text-gray-300 mb-8">
              Achieve a perfectly tailored style from home—no booking or appointments required. Find your next hair transformation today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/try-on"
                className="btn bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Try Now
              </Link>
              <Link
                to="/studio-hair-salon"
                className="btn bg-white/10 hover:bg-white/20 text-white border-2 border-white/20 px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 backdrop-blur-sm"
              >
                View Gallery
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Experience the Future of Hairstyling
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Hairstyles Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Featured Hairstyles
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Get inspired by our most popular transformations
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredStyles.map((style, index) => (
              <motion.div
                key={style.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="aspect-w-3 aspect-h-4">
                  <img
                    src={style.image}
                    alt={style.name}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-white font-semibold text-lg">{style.name}</h3>
                      <p className="text-gray-200 text-sm">{style.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/studio-hair-salon"
              className="inline-flex items-center px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              View All Styles
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-600">
              Transform your look without breaking the bank. Pay only for what you generate.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-8 text-center">
              <h3 className="text-white text-2xl font-bold mb-2">
                Pay Per Generation
              </h3>
              <div className="text-white text-opacity-90">
                Perfect for trying out new styles
              </div>
            </div>
            <div className="p-8">
              <div className="flex justify-center items-baseline mb-8">
                <span className="text-5xl font-bold text-gray-900">$3.99</span>
                <span className="text-gray-600 ml-2">per image</span>
              </div>
              <ul className="space-y-4 mb-8">
                {pricingFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <FaCheck className="text-teal-500 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/signup"
                className="block w-full bg-teal-500 hover:bg-teal-600 text-white text-center px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Get Started
              </Link>
            </div>
          </motion.div>

          <div className="text-center mt-8 text-gray-600">
            <p className="flex items-center justify-center gap-2">
              <FaCreditCard className="text-teal-500" />
              Secure payment processing with Stripe
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
  <div className="container">
    <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
      Frequently Asked Questions
    </h2>
    <div className="max-w-3xl mx-auto space-y-6">
      {[
        {
          question: "How does AI Hairstyle Studio work?",
          answer: (
            <>
              Our AI technology analyzes your facial features and applies your chosen hairstyle
              naturally. Simply upload a photo, select a hairstyle, and our AI will generate a realistic preview of how you'd look.
            </>
          )
        },
        {
          question: "Is my photo data secure?",
          answer: "Yes, we take your privacy seriously. All uploaded photos are processed securely and automatically deleted after processing. We never share your photos with third parties."
        },
        {
          question: "What types of hairstyles can I try?",
          answer: (
            <>
              We offer a wide range of{" "}
              <a
                href="https://www.aihairstylestudio.com/hairstyles"
                className="text-teal-300"
                target="_blank"
                rel="noopener noreferrer"
              >
                hairstyles
              </a>{" "}
              for all genders, including short cuts, long styles, different colors, and trending looks. You can also upload reference photos of specific styles you'd like to try.
            </>
          )
        },
        {
          question: "How realistic are the results?",
          answer: "Our AI is trained on millions of photos to ensure highly realistic results. While results may vary based on photo quality, we strive to provide natural-looking previews that match your facial features."
        },
        {
          question: "Can I save and share my transformations?",
          answer: "Yes! You can save all your generated hairstyles to your gallery and easily share them on social media or download them to your device."
        }
      ].map((faq, index) => (
        <div key={index} className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {faq.question}
          </h3>
          <p className="text-gray-600">{faq.answer}</p>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 relative overflow-hidden">
        <div className="container relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to Transform Your Look?
            </h2>
            <p className="text-xl md:text-2xl mb-8 text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Join thousands of satisfied customers who have found their perfect hairstyle.
            </p>
            <Link
              to="/signup"
              className="inline-block bg-white hover:bg-teal-50 text-teal-600 px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold text-lg"
            >
              Start Your Journey
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  </>
  );
};

export default Home;
